import { FeatureFlag } from "@/models/feature/feature";
import { PATH_KEYS } from "../services/appServices";
import { useItemService } from "./useServices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { restError } from "@/models/client/RestResponse";

const useFeatureFlags = () => {
  /**** Services ****/
  const queryClient = useQueryClient();

  const featureFlagService = useItemService((services) => services.featureFlag);

  /**** Queries & Mutations ****/
  const useFetchTenantFeatureFlags = () =>
    useQuery<FeatureFlag[]>({
      queryKey: [PATH_KEYS.featureFlag, "get"],
      queryFn: async () => {
        const response = await featureFlagService.getTenantFeatureFlags();
        if (response.success) return response.value;
        else throw restError(response);
      },
    });

  const updateTenantFeatureFlag = useMutation<
    FeatureFlag,
    string,
    {
      strategyId: string;
      featureName: string;
      userIds: string[];
      groupIds: string[];
    }
  >({
    mutationKey: [PATH_KEYS.featureFlag, "update"],
    mutationFn: async ({
      strategyId,
      featureName,
      userIds,
      groupIds,
    }: {
      strategyId: string;
      featureName: string;
      userIds: string[];
      groupIds: string[];
    }) => {
      const response = await featureFlagService.updateTenantFeatureFlag(
        strategyId,
        featureName,
        userIds,
        groupIds
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.featureFlag] });
    },
  });

  const removeUserFromFeatureFlag = useMutation<
    FeatureFlag,
    string,
    {
      strategyId: string;
      featureName: string;
      userId: string;
    }
  >({
    mutationKey: [PATH_KEYS.featureFlag, "removeUser"],
    mutationFn: async ({
      strategyId,
      featureName,
      userId,
    }: {
      strategyId: string;
      featureName: string;
      userId: string;
    }) => {
      const response = await featureFlagService.patchRemoveUserFromFeatureFlag(
        strategyId,
        featureName,
        userId
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.featureFlag] });
    },
  });
  const removeAllUsersFromFeatureFlag = useMutation<
    FeatureFlag,
    string,
    {
      strategyId: string;
      featureName: string;
    }
  >({
    mutationKey: [PATH_KEYS.featureFlag, "removeAllUsers"],
    mutationFn: async ({
      strategyId,
      featureName,
    }: {
      strategyId: string;
      featureName: string;
    }) => {
      const response =
        await featureFlagService.patchRemoveAllUsersFromFeatureFlag(
          strategyId,
          featureName
        );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.featureFlag] });
    },
  });
  const removeGroupFromFeatureFlag = useMutation<
    FeatureFlag,
    string,
    {
      strategyId: string;
      featureName: string;
      groupId: string;
    }
  >({
    mutationKey: [PATH_KEYS.featureFlag, "removeGroup"],
    mutationFn: async ({
      strategyId,
      featureName,
      groupId,
    }: {
      strategyId: string;
      featureName: string;
      groupId: string;
    }) => {
      const response = await featureFlagService.patchRemoveGroupFromFeatureFlag(
        strategyId,
        featureName,
        groupId
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.featureFlag] });
    },
  });

  /**** Value ****/
  return {
    useFetchTenantFeatureFlags,
    updateTenantFeatureFlag,
    removeUserFromFeatureFlag,
    removeAllUsersFromFeatureFlag,
    removeGroupFromFeatureFlag,
  };
};

export default useFeatureFlags;
